import { STORE_KEY_ID_LIST, storeGet } from 'mailup-react-helpers'

import {
  beRecipientApiFindAndReplace,
  beRecipientApiFindAndReplaceSimulation,
} from 'Api/beRecipientApiTS'

import { beAsyncTasksApiGetTask } from 'Api/beAsyncTasksApiTS'

import type { FindAndReplaceSimulationResultDto } from 'Types/beRecipientsApi'

import type {
  ModalBulkFindReplaceState,
  SearchFiltersState,
  SelectionState,
} from 'Pages/RecipientsList/model'

import {
  searchFiltersToConditions,
  toSubscriptionStatus,
} from 'Pages/RecipientsList/recoil/services/results'

export const fetchFindReplacePreview = async (
  {
    mode,
    checked,
    searchFilters,
  }: {
    mode: SelectionState['mode']
    checked: SelectionState['checked']
    searchFilters: SearchFiltersState
  },
  {
    FindColumn,
    FindText,
    FindCaseSensitive,
    FindType,
    ReplaceText,
  }: Pick<
    ModalBulkFindReplaceState,
    'FindColumn' | 'FindText' | 'FindCaseSensitive' | 'FindType' | 'ReplaceText'
  >,
): Promise<number> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const { IdTask } = await beRecipientApiFindAndReplaceSimulation(idList, {
    Conditions:
      mode === 'all' ? searchFiltersToConditions(searchFilters) : undefined,
    SubscriptionStatus:
      mode === 'all'
        ? toSubscriptionStatus(searchFilters.status.value)
        : undefined,
    RecipientIds: mode === 'all' ? undefined : checked,

    //
    FindColumn: FindColumn as NonNullable<
      ModalBulkFindReplaceState['FindColumn']
    >,
    FindCaseSensitive,
    FindType,
    FindText,
    ReplaceText,
  })

  return IdTask
}

interface PreviewCompleted {
  previewStatus: 'ready'
  items: ModalBulkFindReplaceState['items']
  totalItems: ModalBulkFindReplaceState['totalItems']
}

interface PreviewError {
  previewStatus: 'error'
}

interface PreviewPending {
  pollingIteration: number
}

export const fetchPreviewDetails = async (
  idTask: number,
  pollingIteration: number,
): Promise<PreviewCompleted | PreviewError | PreviewPending> => {
  const { Status, Metadata } = await beAsyncTasksApiGetTask(idTask)

  if (Status === 'Completed') {
    const {
      SimulationResult: { Items },
    } = JSON.parse(Metadata) as {
      SimulationResult: {
        Items: FindAndReplaceSimulationResultDto[]
      }
    }
    return {
      previewStatus: 'ready',
      items: Items.map((o) => ({
        id: o.Id,
        oldValue: o.OldValue,
        newValue: o.NewValue,
      })),
      totalItems: Items.length,
    }
  } else if (Status === 'Error' || Status === 'Aborted') {
    return {
      previewStatus: 'error',
    }
  } else {
    return {
      pollingIteration: pollingIteration + 1,
    }
  }
}

export const invokeFindReplace = async (
  {
    mode,
    checked,
    searchFilters,
  }: {
    mode: SelectionState['mode']
    checked: SelectionState['checked']
    searchFilters: SearchFiltersState
  },
  {
    FindColumn,
    FindText,
    FindCaseSensitive,
    FindType,
    ReplaceText,
  }: Pick<
    ModalBulkFindReplaceState,
    'FindColumn' | 'FindText' | 'FindCaseSensitive' | 'FindType' | 'ReplaceText'
  >,
): Promise<number> => {
  if (FindColumn === undefined) {
    return 0
  }

  const idList = storeGet(STORE_KEY_ID_LIST)

  const { IdTask } = await beRecipientApiFindAndReplace(idList, {
    Conditions:
      mode === 'all' ? searchFiltersToConditions(searchFilters) : undefined,
    SubscriptionStatus:
      mode === 'all'
        ? toSubscriptionStatus(searchFilters.status.value)
        : undefined,
    RecipientIds: mode === 'all' ? undefined : checked,
    //
    FindColumn,
    FindCaseSensitive,
    FindType,
    FindText,
    ReplaceText,
  })

  return IdTask
}
